/**
 * useGroupDetails — loads a single group's header, members and stats through
 * the shared groupService (mobile groupDetails parity, web-only state shape).
 */
import { useCallback, useEffect, useState } from 'react';
import groupService, { type GroupDetailResult } from '@wlb/shared/api/services/groupService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';

export interface UseGroupDetailsResult {
  detail: GroupDetailResult | null;
  loading: boolean;
  error: string;
  reload: () => void;
}

export function useGroupDetails(groupId?: string): UseGroupDetailsResult {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<GroupDetailResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!groupId) {
      setDetail(null);
      setLoading(false);
      setError(t('groupDetails.errors.missingId'));
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    groupService
      .getGroupDetails(groupId)
      .then(result => {
        if (cancelled) return;
        setDetail(result);
      })
      .catch(() => {
        if (cancelled) return;
        setDetail(null);
        setError(t('groupDetails.errors.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [groupId, reloadToken, t]);

  return { detail, loading, error, reload };
}
